/**
 * Helper functions para extraer y procesar información de referidos
 */

/**
 * Extrae los emails de referidos invitados desde el campo widget "refe" (ID: 131)
 * Los emails están separados por saltos de línea (\n)
 *
 * @param rawAnswers - Respuestas raw del formulario principal
 * @returns Array de emails en minúsculas y sin espacios
 *
 * @example
 * // Input: { '131': { answer: "email1@dom.com\nemail2@dom.com" } }
 * // Output: ['email1@dom.com', 'email2@dom.com']
 */
export function extractInvitedReferralEmails(rawAnswers: Record<string, any> | null | undefined): string[] {
  if (!rawAnswers) return [];

  // Campo 131 contiene el widget de referidos
  const widget = rawAnswers['131'];
  if (!widget?.answer || typeof widget.answer !== 'string') return [];

  // Split por saltos de línea y filtrar emails válidos
  return widget.answer
    .split('\n')
    .map((line: string) => line.trim().toLowerCase())
    .filter((line: string) => line.includes('@') && line.length > 5);
}

/**
 * Extrae el tipo de relación del referido de los raw_answers de JotForm
 * El campo en JotForm es "137" con name "rcualEs"
 */
export const extractReferralType = (rawAnswers: Record<string, any> | undefined): string | null => {
  if (!rawAnswers) return null;

  // Buscar el campo específico por ID o name
  // El campo puede estar como "137" o con el name "rcualEs"
  const relationshipValue = rawAnswers['137']?.answer || rawAnswers['rcualEs']?.answer;

  if (relationshipValue && typeof relationshipValue === 'string') {
    return relationshipValue.trim();
  }

  // Búsqueda alternativa por text que contenga "relación"
  for (const key in rawAnswers) {
    const field = rawAnswers[key];
    if (field?.text && typeof field.text === 'string' && field.text.toLowerCase().includes('relación')) {
      if (field?.answer && typeof field.answer === 'string') return field.answer.trim();
      if (field?.answer != null) return String(field.answer).trim();
      return null;
    }
  }

  return null;
};

/**
 * Mapea el tipo de relación a configuración visual (color, ícono, label)
 */
export const getReferralTypeConfig = (type: string | null): {
  color: string;
  bgColor: string;
  icon: string;
  label: string;
} => {
  if (!type) {
    return {
      color: 'text-gray-700',
      bgColor: 'bg-gray-100',
      icon: '❓',
      label: 'No especificado',
    };
  }

  // Normalizar el tipo (quitar acentos, lowercase)
  const rawTypeStr = typeof type === 'string' ? type.trim() : String(type);
  const normalizedType = rawTypeStr.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // Mapeo de tipos a configuración visual
  const typeMap: Record<string, any> = {
    'lider': {
      color: 'text-blue-700',
      bgColor: 'bg-blue-100',
      icon: '👔',
      label: 'Líder (jefe)',
    },
    'compañero par': {
      color: 'text-purple-700',
      bgColor: 'bg-purple-100',
      icon: '🤝',
      label: 'Compañero par',
    },
    'companero par': {
      color: 'text-purple-700',
      bgColor: 'bg-purple-100',
      icon: '🤝',
      label: 'Compañero par',
    },
    'subordinado': {
      color: 'text-green-700',
      bgColor: 'bg-green-100',
      icon: '📈',
      label: 'Subordinado',
    },
    'otro': {
      color: 'text-orange-700',
      bgColor: 'bg-orange-100',
      icon: '•',
      label: 'Otro',
    },
  };

  // Buscar coincidencia
  const config = typeMap[normalizedType];
  if (config) {
    return config;
  }

  // Fallback: devolver el tipo tal cual con estilo por defecto
  return {
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
    icon: '•',
    label: type,
  };
};
